/**
 * Cross-platform routine helpers, so a routine reads and links the same in the
 * web app and on the phone. Pure logic, no framework imports — which trigger
 * gets which icon lives here, while resolving that name to an icon *component*
 * stays platform-local, since web draws from `lucide-react` and native from
 * `lucide-react-native`.
 */

import type {
  EmailLabelColor,
  RoutineResp,
  SlackTriggerAvailabilityResp,
  SlackTriggerChannel,
  TriggerConfig,
} from "@/types/routines.ts";
import { isAxiosError } from "axios";
import { formatCronShort } from "./cronUtils.ts";
import { addDays, dateOf, zonedMidnightMs } from "./dayBounds.ts";
import { offsetMinutesLabel } from "./duration.ts";

/**
 * The server's own words when it has any. Refusing a routine whose end time has
 * passed names the date and what to change — a generic "failed to update" would
 * leave the user flipping a switch that keeps sliding back with no explanation.
 * The fallback differs by call site, since the same refusal reaches the user
 * from a toggle, a save, and a create.
 */
export const routineErrorMessage = (err: unknown, fallback = "Failed to update routine"): string =>
  (isAxiosError<{ error?: string }>(err) && err.response?.data?.error) || fallback;

/** Display swatch per semantic color token (mirrors the provider palettes). */
export const labelColorHex: Record<EmailLabelColor, string> = {
  gray: "#cccccc",
  red: "#fb4c2f",
  orange: "#ffad47",
  yellow: "#fad165",
  green: "#16a766",
  teal: "#43d692",
  blue: "#4a86e8",
  sky: "#a4c2f4",
  purple: "#a479e2",
  pink: "#f691b3",
};

/** The icons a trigger can be drawn with, as semantic names. */
export type TriggerIconName = "mail" | "tag" | "clock" | "calendar" | "message";

/**
 * Which icon stands for a trigger.
 *
 * A NAME, not a component: web draws from `lucide-react` and native from
 * `lucide-react-native`, so the mapping is the shared part and the component is
 * not. The form, the routines list, and the artifact header all ask this
 * question; answering it in one place is what stops a trigger from carrying the
 * right icon on one surface and a stale fallback on another.
 */
export const triggerIconName = (type: TriggerConfig["type"]): TriggerIconName => {
  switch (type) {
    case "email":
      return "mail";
    case "emailLabeled":
      return "tag";
    case "slackMessage":
    case "slackDirectMessage":
    case "slackMention":
      return "message";
    case "cron":
      return "clock";
    default:
      return "calendar";
  }
};

/** Short label for a routine's trigger, as shown on list rows and detail pages. */
export const getTriggerLabel = (config: TriggerConfig): string => {
  switch (config.type) {
    case "email":
      return "Email";
    case "emailLabeled":
      // Named rather than counted while there's one label, since that's the
      // common case and the name is the whole point of the routine.
      return config.labels.length === 1
        ? `Labeled "${config.labels[0]}"`
        : `Labeled (${config.labels.length})`;
    case "beforeMeeting":
      return `${offsetMinutesLabel(config.minutesBefore)} before meeting`;
    case "afterMeeting":
      return `${offsetMinutesLabel(config.minutesAfter)} after meeting`;
    case "inviteReceived":
      return "Invite received";
    case "inviteUpdated":
      return "Invite updated";
    case "rsvpResponse":
      return "RSVP response";
    case "eventCancelled":
      return "Event cancelled";
    case "cron":
      return formatCronShort(config.schedule);
    case "slackMessage":
      return `Slack: #${config.channelName}`;
    case "slackDirectMessage":
      return "Slack DMs";
    case "slackMention":
      return "Slack mentions";
    case "newMail":
      return "New mail";
  }
};

/**
 * Whether a routine can be fired by hand, as offered by the Run control on its
 * detail page.
 *
 * Only on a schedule. A cron routine has no gap between a manual run and a real
 * one — "now" is a legitimate instance of its trigger, so firing it early is just
 * an early run. Every other trigger fires *in response to something*, and a manual
 * run has no such something: the backend invents one, writing an LLM-authored email
 * or calendar event as a real artifact and letting the routine act on an event that
 * never happened. On an email routine that surfaces to the user as a drafted reply
 * to a correspondent who doesn't exist.
 *
 * This also keeps action-kind routines out (they trigger on "newMail"), which is
 * what we want for a second reason: they run inline and never open a thread, so a
 * manual run would open one they have no use for.
 */
export const canRunRoutineManually = (config: TriggerConfig) => config.type === "cron";

/**
 * The trigger types a routine form offers, and how each is phrased as a
 * condition. "newMail" is excluded: it belongs to action-kind routines (email
 * labeling), which aren't created or edited through the routine form, so it must
 * not appear in a picker built from these labels.
 */
export const triggerTypeLabels: Record<Exclude<TriggerConfig["type"], "newMail">, string> = {
  email: "I receive an email",
  emailLabeled: "An email is labeled",
  beforeMeeting: "Before a meeting",
  afterMeeting: "After a meeting",
  inviteReceived: "I receive a calendar invite",
  inviteUpdated: "A calendar invite is updated",
  rsvpResponse: "Someone RSVPs to my event",
  eventCancelled: "An event is cancelled",
  cron: "On a schedule",
  slackMessage: "A Slack message is posted",
  slackDirectMessage: "Someone DMs me on Slack",
  slackMention: "I'm mentioned on Slack",
};

/**
 * Slack triggers the forms can't set up: only the agent can. A routine that
 * already carries one keeps it listed, so its picker doesn't read as blank.
 */
const AGENT_ONLY_TRIGGER_TYPES: ReadonlySet<TriggerConfig["type"]> = new Set([
  "slackDirectMessage",
  "slackMention",
]);

/** A trigger type the routine form's picker can offer. */
export type SelectableTriggerType = keyof typeof triggerTypeLabels;

/**
 * The trigger types a picker should offer, in the order it lists them.
 *
 * The Slack trigger needs a Slack connection, so it is offered only once the
 * server says it can be set up — or needs a reconnect first, which the form
 * prompts for. A routine that already carries it keeps it listed regardless:
 * a picker that cannot show the routine's own trigger would read as blank.
 */
export const selectableTriggerTypes = (
  availability: SlackTriggerAvailabilityResp | undefined,
  currentType: TriggerConfig["type"],
): SelectableTriggerType[] => {
  const slackOffered =
    availability?.status === "ready" ||
    availability?.status === "reconnect" ||
    currentType === "slackMessage";
  return (Object.keys(triggerTypeLabels) as SelectableTriggerType[]).filter(
    (type) =>
      (type !== "slackMessage" || slackOffered) &&
      (!AGENT_ONLY_TRIGGER_TYPES.has(type) || type === currentType),
  );
};

/**
 * Whether a trigger has everything it needs to be saved. A blank Slack trigger
 * names no channel, and the server would refuse it with nothing on the form
 * pointing at why.
 */
export const isTriggerConfigComplete = (config: TriggerConfig): boolean =>
  config.type !== "slackMessage" || config.channelId !== "";

type SlackTriggerConfig = Extract<TriggerConfig, { type: "slackMessage" }>;

/**
 * The channels a Slack trigger's picker lists. A saved channel stays listed
 * even when the fetched list no longer carries it — the user left it, or the
 * list hasn't loaded — so the picker never reads as blank over a routine that
 * does watch a channel.
 */
export const slackChannelOptions = (
  channels: SlackTriggerChannel[],
  config: Pick<SlackTriggerConfig, "channelId" | "channelName">,
): SlackTriggerChannel[] => {
  if (!config.channelId || channels.some((c) => c.id === config.channelId)) return channels;
  return [{ id: config.channelId, name: config.channelName, isPrivate: false }, ...channels];
};

/** What the channel picker reads as before a channel is chosen. */
export const slackChannelPlaceholder = ({
  isLoading,
  isError,
}: {
  isLoading: boolean;
  isError: boolean;
}): string => {
  if (isLoading) return "Loading channels...";
  if (isError) return "Couldn't load channels";
  return "Pick a channel";
};

/** The Slack trigger's copy for when there is no connection to pick a channel with. */
export const slackTriggerCopy = {
  notConnected: "Slack is not connected",
  reconnect: "Reconnect Slack to use channel triggers",
} as const;

/** The RSVP a "someone RSVPs to my event" routine waits for. */
export type RsvpResponseStatus = Extract<TriggerConfig, { type: "rsvpResponse" }>["responseStatus"];

/**
 * How each RSVP reads, in the order the picker offers them. Shared so the phone
 * and the web form name the same four answers the same way: the stored value is
 * the key, so a label edited on one side alone would have the two apps
 * describing the same filter differently.
 */
export const rsvpResponseLabels: Record<RsvpResponseStatus, string> = {
  accepted: "Accepted",
  declined: "Declined",
  tentative: "Tentative",
  any: "Any",
};

/**
 * The same four answers as the order a picker offers them in. Separate from the
 * labels because it answers a different question — the labels say what each one
 * reads as, this says which comes first — and `satisfies` means a status
 * misspelled here fails to compile rather than rendering an empty row.
 */
const RSVP_RESPONSE_ORDER = [
  "accepted",
  "declined",
  "tentative",
  "any",
] as const satisfies readonly RsvpResponseStatus[];

/** The answers as a picker wants them: a value to store and a label to show. */
export const rsvpResponseOptions = RSVP_RESPONSE_ORDER.map((value) => ({
  value,
  label: rsvpResponseLabels[value],
}));

/**
 * Whether a string is one of the four answers. Both apps read this value back
 * as a bare string — off a `<Select>` on the web, off an artifact view's select
 * component on the phone — and neither can be trusted to have narrowed it.
 */
export const isRsvpResponseStatus = (value: string): value is RsvpResponseStatus =>
  Object.hasOwn(rsvpResponseLabels, value);

/**
 * How the classifier field is worded, by what the trigger fires on. The field
 * asks the user to describe, in English, which emails or which meetings the
 * routine should act on.
 *
 * Shared because the phone and the web form are two renderings of one field: the
 * example is what teaches someone what to type there, and a copy edit to one
 * side otherwise leaves the other quietly asking for something different.
 */
export type ClassifierCopy = { description: string; placeholder: string };

const EMAIL_CLASSIFIER: ClassifierCopy = {
  description: "Describe in English what this email should be about",
  placeholder:
    "e.g. A receipt or invoice from a vendor that I need to pay, or a newsletter I subscribe to",
};

const SLACK_CLASSIFIER: ClassifierCopy = {
  description: "Describe in English what this message should be about",
  placeholder: "e.g. A customer asking for help, or a question about the release",
};

const EVENT_CLASSIFIER: ClassifierCopy = {
  description: "Describe in English what this event should be about",
  placeholder: "e.g. A 1:1 with my manager, or an external sales call with a prospect",
};

/** The triggers that fire on a piece of mail rather than on a calendar event. */
const MAIL_TRIGGERS: ReadonlySet<TriggerConfig["type"]> = new Set([
  "email",
  "emailLabeled",
  "newMail",
]);

/**
 * A Slack trigger fires on a message and is worded as one. Every other trigger
 * outside that set fires on a calendar event, so the event wording is the
 * default rather than a case per calendar trigger — a new one is worded
 * correctly the moment it exists.
 */
export const classifierCopy = (type: TriggerConfig["type"]): ClassifierCopy => {
  if (type === "slackMessage" || type === "slackDirectMessage" || type === "slackMention") {
    return SLACK_CLASSIFIER;
  }
  return MAIL_TRIGGERS.has(type) ? EMAIL_CLASSIFIER : EVENT_CLASSIFIER;
};

/**
 * How a trigger's condition is phrased on its own, without its parameters — the
 * label of the row a routine's trigger occupies.
 *
 * `newMail` has no entry in `triggerTypeLabels`: it belongs to the routines that
 * run inline on extraction rather than through the form's picker, so it's named
 * here instead of widening a map the picker is built from.
 */
export const triggerConditionLabel = (config: TriggerConfig) =>
  config.type === "newMail" ? "New mail" : triggerTypeLabels[config.type];

/**
 * The trigger's parameter, phrased to sit beside its condition — the meeting
 * offset, and nothing for the triggers that take no parameter.
 *
 * Separate from `getTriggerLabel`, which folds both into one phrase for a list
 * row. A detail row states the condition and its parameter as two columns, so it
 * needs them apart.
 */
export const triggerParamLabel = (config: TriggerConfig) => {
  switch (config.type) {
    case "beforeMeeting":
      return offsetMinutesLabel(config.minutesBefore);
    case "afterMeeting":
      return offsetMinutesLabel(config.minutesAfter);
    default:
      return null;
  }
};

/** The trigger types that fire relative to a meeting, and so carry an offset. */
export type MeetingOffsetTrigger = "beforeMeeting" | "afterMeeting";

export type MeetingOffsetConfig = Extract<TriggerConfig, { type: MeetingOffsetTrigger }>;

/**
 * The same trigger with its offset moved, writing whichever minutes field it
 * carries. Every offset control goes through here rather than branching on the
 * type itself — the two fields are easy to mix up, and a control that wrote the
 * wrong one would silently move the trigger to the other side of the meeting.
 */
export const withMeetingOffset = (config: MeetingOffsetConfig, minutes: number) =>
  config.type === "beforeMeeting"
    ? { ...config, minutesBefore: minutes }
    : { ...config, minutesAfter: minutes };

const HALF_HOUR = 30;
const HOUR = 60;
const DAY = 24 * HOUR;

/**
 * How long before a meeting a routine can be set to fire: minutes while the
 * lead time is under an hour, then every half hour out to a full day.
 *
 * A day out is a real setting — a brief on a 9am meeting wants writing the
 * evening before, not at 8am. The half-hour grid holds the whole way rather
 * than widening as it goes, so a lead time in between is picked rather than
 * rounded to whichever coarse offset happened to be offered near it.
 */
const SUB_HOUR_OFFSETS = [10, 15, 30, 45];
const HALF_HOUR_OFFSETS = Array.from(
  { length: (DAY - HOUR) / HALF_HOUR + 1 },
  (_, i) => HOUR + i * HALF_HOUR,
);

const MEETING_OFFSET_PRESETS: Record<MeetingOffsetTrigger, number[]> = {
  beforeMeeting: [...SUB_HOUR_OFFSETS, ...HALF_HOUR_OFFSETS],
  // A follow-up wants to run while the meeting is still fresh, so the offsets
  // worth offering start closer in than the ones before one do.
  afterMeeting: [5, 10, 15, 30, 60],
};

/**
 * The offsets a meeting trigger is offered, as picker options.
 *
 * Widened to include `current` when it isn't one of the presets: a routine can
 * ship any offset, and a form that takes a free number can set one, so the value
 * in force has to stay visible and selected rather than silently reading as a
 * neighbouring preset.
 */
export const meetingOffsetOptions = (type: MeetingOffsetTrigger, current: number) => {
  const presets = MEETING_OFFSET_PRESETS[type];
  const offsets = presets.includes(current) ? presets : [...presets, current].sort((a, b) => a - b);
  return offsets.map((minutes) => ({ value: minutes, label: offsetMinutesLabel(minutes) }));
};

/**
 * A blank trigger of the given type.
 *
 * Switching a routine's trigger type replaces the whole config rather than
 * merging it. The two mail triggers do overlap — they share the header-shaped
 * conditions — so a switch between them discards From/To/Cc/Subject/About along
 * with the fields that genuinely do not carry across. That is a deliberate
 * simplification, not a property of the union: merging would have to decide what
 * a half-filled config means on the other side, and a blank form is the honest
 * answer while nothing tells the user their conditions were kept.
 */
export const defaultTriggerConfig = (type: TriggerConfig["type"]): TriggerConfig => {
  switch (type) {
    case "email":
      return { type: "email" };
    // Seeded with "action needed" rather than an empty list: the schema requires
    // at least one label, so a blank trigger would be unsaveable, and this is the
    // label most routines of this shape want.
    case "emailLabeled":
      return { type: "emailLabeled", labels: ["action needed"] };
    case "beforeMeeting":
      return { type: "beforeMeeting", minutesBefore: 15 };
    case "afterMeeting":
      return { type: "afterMeeting", minutesAfter: 5 };
    case "inviteReceived":
      return { type: "inviteReceived" };
    case "inviteUpdated":
      return { type: "inviteUpdated" };
    case "rsvpResponse":
      return { type: "rsvpResponse", responseStatus: "any" };
    case "eventCancelled":
      return { type: "eventCancelled" };
    case "cron":
      return { type: "cron", schedule: "0 9 * * 1-5" };
    // Blank until the user picks a channel; `isTriggerConfigComplete` keeps it
    // from being saved that way.
    case "slackMessage":
      return { type: "slackMessage", teamId: "", channelId: "", channelName: "" };
    // Only offered on a routine that already has it, which the form doesn't
    // reset to a default; present only to keep this switch exhaustive.
    case "slackDirectMessage":
      return { type: "slackDirectMessage", teamId: "", slackUserId: "" };
    case "slackMention":
      return { type: "slackMention", teamId: "", slackUserId: "" };
    // Not user-selectable (excluded from the trigger picker); present only to
    // keep this switch exhaustive over TriggerConfig.
    case "newMail":
      return { type: "newMail" };
  }
};

/**
 * A routine's end time is stored as an instant, but people set it as a day —
 * "watch eBay for a week", not "until 09:00 next Monday". Both forms therefore
 * read and write it through a date picker, and these are the conversion.
 *
 * The end of a day is midnight opening the next one, taken in the user's own
 * zone. Going through `zonedMidnightMs` rather than adding 24 hours keeps a
 * clock-change day honest.
 */

/**
 * The last day the routine runs, as `YYYY-MM-DD` in the user's zone, or null
 * when there is no readable end time.
 *
 * A stored end time is a boundary instant, and when it lands exactly on a
 * midnight the day it belongs to is the one before: a routine ending "the 18th
 * at 00:00" is one that ran through the 17th. Both the picker and the label go
 * through this, so what the user picks is what they read back — computing it in
 * only one of them is how a form comes to show a date one day off the one that
 * was saved.
 */
const lastRunDay = (endsAt: string, timeZone: string): string | null => {
  const ms = new Date(endsAt).getTime();
  if (Number.isNaN(ms)) return null;
  const date = dateOf(ms, timeZone);
  return ms === zonedMidnightMs(date, timeZone) ? addDays(date, -1) : date;
};

/** The `YYYY-MM-DD` a date input should show for a stored end time. */
export const endsAtToDateInput = (endsAt: string | undefined, timeZone: string): string => {
  if (!endsAt) return "";
  return lastRunDay(endsAt, timeZone) ?? "";
};

/**
 * The day a calendar picker should open on for a stored end time, as a local
 * `Date` — which is what a picker comparing days with `isSameDay` needs.
 *
 * Native needs the day as a `Date` where web needs it as a string, and deriving
 * it there from the stored instant is how the picker came to open a day past
 * the label beside it. Both forms go through `lastRunDay` from here instead.
 * Null means no end time, and the caller decides what to open on.
 */
export const endsAtToPickerDay = (endsAt: string | undefined, timeZone: string): Date | null => {
  const day = endsAtToDateInput(endsAt, timeZone);
  if (!day) return null;
  const [year, month, date] = day.split("-").map(Number);
  // Built from parts rather than parsed: `new Date("YYYY-MM-DD")` reads as UTC
  // midnight, which is the previous day anywhere west of Greenwich.
  return new Date(year, month - 1, date);
};

/**
 * The instant a `YYYY-MM-DD` from a date input ends at: midnight opening the
 * following day, so a routine set to end on the 17th still runs through all of
 * the 17th. Empty input means no end time.
 */
export const dateInputToEndsAt = (date: string, timeZone: string): string | undefined => {
  if (!date) return undefined;
  return new Date(zonedMidnightMs(addDays(date, 1), timeZone)).toISOString();
};

/** A stored end time as a person reads it: the last day the routine runs. */
export const formatEndsAt = (endsAt: string | undefined, timeZone: string): string | null => {
  const display = endsAt ? lastRunDay(endsAt, timeZone) : null;
  if (!display) return null;
  const [year, month, day] = display.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** Marks a bound routine id as a system routine rather than an ent. */
const SYSTEM_ROUTINE_PREFIX = "system:";

/**
 * A list key that stays unique across both places a routine comes from.
 *
 * A shipped routine is keyed by its registry slug and a
 * routine they own by its ent id — two id spaces in one list, so a bare `id`
 * would collide the day a slug matched an ent id. Every list of routines needs
 * the same answer, so it is asked once.
 */
export const routineRowKey = (routine: Pick<RoutineResp, "id" | "kind">) =>
  routine.kind === "system" ? `${SYSTEM_ROUTINE_PREFIX}${routine.id}` : routine.id;

/**
 * The bare id the routine detail route keys on. Threads and artifacts carry an
 * system routine as the `system:<slug>` sentinel, while the route (and
 * the routines list it's reached from) uses the plain slug.
 */
export const routineRouteId = (routineEntId: string): string =>
  routineEntId.startsWith(SYSTEM_ROUTINE_PREFIX)
    ? routineEntId.slice(SYSTEM_ROUTINE_PREFIX.length)
    : routineEntId;

/** How the routines list is narrowed: what okthx ships, what the user made, or both. */
export type RoutineFilter = "system" | "custom" | "all";

export const routineFilterLabels: Record<RoutineFilter, string> = {
  system: "System",
  custom: "Custom",
  all: "All",
};

/** A routine okthx ships, as opposed to one the user wrote. */
const isSystemAuthored = (routine: RoutineResp) => routine.kind === "system";

export const matchesRoutineFilter = (routine: RoutineResp, filter: RoutineFilter) => {
  switch (filter) {
    case "all":
      return true;
    case "system":
      return isSystemAuthored(routine);
    case "custom":
      return !isSystemAuthored(routine);
  }
};
